import { CompetitionFilterTypes } from "~pages/Competitions/CompetitionsListPage/enums/competitionFilterTypes.ts";
import { CompetitionTableColumns } from "~pages/Competitions/CompetitionsListPage/enums/competitionTableColumns.ts";
import { ConductTableColumns } from "~pages/Conducts/ConductListPage/enums/conductTableColumns.ts";
import { ConductTableFilterTypes } from "~pages/Conducts/ConductListPage/enums/conductTableFilterTypes.ts";
import { EntityTableColumns } from "~pages/Entities/EntitiesListPage/enums/entityTableColumns.ts";
import { EntityTableFiltersTypes } from "~pages/Entities/EntitiesListPage/enums/entityTableFiltersTypes.ts";
import { EntityGroupTableColumns } from "~pages/EntityGroups/EntityGroupListPage/enums/entityGroupTableColumns.ts";
import { EntityGroupTableFilterTypes } from "~pages/EntityGroups/EntityGroupListPage/enums/entityGroupTableFilterTypes.ts";
import { FixturesFilterTypes } from "~pages/Fixtures/FixtureListPage/types";
import { FixtureTableColumns } from "~pages/Fixtures/FixtureListPage/types/fixtureColumnsEnum.ts";
import { LeaderCriteriaTableColumns } from "~pages/LeaderCriteria/LeaderCriteriaListPage/enums/leaderCriteriaTableColumns.ts";
import { LeaderCriteriaRuleTableColumns } from "~pages/LeaderCriteria/LeaderCriteriaRuleListPage/enums/leaderCriteriaRuleTableColumns.ts";
import { LeagueTableColumns } from "~pages/Leagues/LeagueListPage/enums/leagueTableColumns.ts";
import { LeagueTableFilters } from "~pages/Leagues/LeagueListPage/enums/leagueTableFilters.ts";
import { SeasonOfficialsTableColumns } from "~pages/Officials/SeasonOfficialsListPage/enums/seasonOfficialsTableColumns.ts";
import { SeasonOfficialsTableFilters } from "~pages/Officials/SeasonOfficialsListPage/enums/seasonOfficialsTableFilters.ts";
import { PersonTableColumns } from "~pages/Persons/PersonListPage/enums/personTableColumns.ts";
import { PersonTableFiltersTypes } from "~pages/Persons/PersonListPage/enums/personTableFiltersTypes.ts";
import { FixturePlayByPlayEventColumns } from "~pages/Reports/components/FixturePlayByPlayFlaggedEventDataTable/enums/FixturePlayByPlayEventColumns.ts";
import { FixturePlayByPlayEventFilters } from "~pages/Reports/components/FixturePlayByPlayFlaggedEventDataTable/enums/FixturePlayByPlayEventFilters.ts";
import { RosterTableColumns } from "~pages/Roster/enums/rosterTableColumns.ts";
import { FixtureRosterTableColumns } from "~pages/Roster/FixtureRosterListPage/enums/fixtureRosterTableColumns.ts";
import { SeasonRosterTableColumns } from "~pages/Roster/SeasonRosterListPage/enums/seasonRosterTableColumns.ts";
import { SeasonEntityTableColumns } from "~pages/SeasonEntities/SeasonEntityListPage/enums/seasonEntityTableColumns.ts";
import { SeasonEntityTableFilters } from "~pages/SeasonEntities/SeasonEntityListPage/enums/seasonEntityTableFilters.ts";
import { SeasonPoolTableColumns } from "~pages/SeasonPools/SeasonPoolListPage/enums/seasonPoolTableColumns.ts";
import { SeasonStageTableColumns } from "~pages/SeasonStages/SeasonStageListPage/enums/seasonStageTableColumns.ts";
import { SeasonTableColumns } from "~pages/Seasons/SeasonListPage/enums/seasonTableColumns.ts";
import { SeasonTableFilterTypes } from "~pages/Seasons/SeasonListPage/enums/seasonTableFilterTypes.ts";
import { SiteTableColumns } from "~pages/Sites/SiteListPage/enums/siteTableColumns.ts";
import { SiteTableFilters } from "~pages/Sites/SiteListPage/enums/siteTableFilters.ts";
import { StandingsConfigurationTableColumns } from "~pages/StandingsConfigurations/StandingsConfigurationListPage/enums/standingsConfigurationTableColumns.ts";
import { VenueTableColumns } from "~pages/Venues/VenueListPage/enums/venueTableColumns.ts";
import { VenueTableFilterTypes } from "~pages/Venues/VenueListPage/enums/venueTableFilterTypes.ts";
import type { DataTableColumnGroupDefinition } from "~types/dataTableColumnGroupDefinition.ts";
import type { UiConfiguration } from "~types/uiConfiguration.ts";

const fixtureTableColumnsArray: Array<DataTableColumnGroupDefinition> = [
  {
    columnChooserTitleIntlKey: "fixtures.table.column.chooser.title.details",
    columns: [
      { key: FixtureTableColumns.FixtureId },
      { key: FixtureTableColumns.ExternalId },
      { key: FixtureTableColumns.RoundNumber, defaultInColumnChooser: true },
      { key: FixtureTableColumns.HomeCompetitor, defaultInColumnChooser: true },
      { key: FixtureTableColumns.Result, defaultInColumnChooser: true },
      { key: FixtureTableColumns.AwayCompetitor, defaultInColumnChooser: true },
      { key: FixtureTableColumns.FixtureNumber, defaultInColumnChooser: true },
      { key: FixtureTableColumns.NameLocal },
      { key: FixtureTableColumns.NameLatin },
      { key: FixtureTableColumns.VenueId, defaultInColumnChooser: true },
      { key: FixtureTableColumns.StartDate, defaultInColumnChooser: true },
      { key: FixtureTableColumns.StartTime, defaultInColumnChooser: true },
      { key: FixtureTableColumns.FixtureType, defaultInColumnChooser: true },
      { key: FixtureTableColumns.Status, defaultInColumnChooser: true },
      { key: FixtureTableColumns.Stage, defaultInColumnChooser: true },
      { key: FixtureTableColumns.StageCode },
      { key: FixtureTableColumns.Pool, defaultInColumnChooser: true },
      { key: FixtureTableColumns.PoolCode },
      { key: FixtureTableColumns.Series },
      { key: FixtureTableColumns.SeriesCode },
      { key: FixtureTableColumns.Added },
      { key: FixtureTableColumns.Attendance },
      { key: FixtureTableColumns.Discipline },
      { key: FixtureTableColumns.StartTimeActualUtc },
      { key: FixtureTableColumns.EndTimeActualUtc },
      { key: FixtureTableColumns.FixtureProfile },
      { key: FixtureTableColumns.ProfileId },
      { key: FixtureTableColumns.InternationalReference },
      { key: FixtureTableColumns.Round },
      { key: FixtureTableColumns.RoundCode },
      { key: FixtureTableColumns.SeriesFixtureNumber },
      { key: FixtureTableColumns.TimesUnconfirmed },
      { key: FixtureTableColumns.StartTimeUtc },
    ],
  },
];

const competitionTableColumnsArray: Array<DataTableColumnGroupDefinition> = [
  {
    columnChooserTitleIntlKey: "competitions.table.column.chooser.title.details",
    columns: [
      { key: CompetitionTableColumns.CompetitionId },
      { key: CompetitionTableColumns.ExternalId },
      { key: CompetitionTableColumns.Images, defaultInColumnChooser: true },
      { key: CompetitionTableColumns.NameLocal, defaultInColumnChooser: true },
      { key: CompetitionTableColumns.NameLatin },
      { key: CompetitionTableColumns.AbbreviationLocal, defaultInColumnChooser: true },
      { key: CompetitionTableColumns.AbbreviationLatin },
      { key: CompetitionTableColumns.AgeGroup, defaultInColumnChooser: true },
      { key: CompetitionTableColumns.InternationalReference },
      { key: CompetitionTableColumns.League },
    ],
  },
];

const seasonOfficialsTableColumnsArray: Array<DataTableColumnGroupDefinition> = [
  {
    columnChooserTitleIntlKey: "seasonOfficials.table.column.chooser.title.details",
    columns: [
      { key: SeasonOfficialsTableColumns.Logo },
      { key: SeasonOfficialsTableColumns.NameFullLocal, defaultInColumnChooser: true },
      { key: SeasonOfficialsTableColumns.PersonId, defaultInColumnChooser: true },
      { key: SeasonOfficialsTableColumns.ExternalId },
      { key: SeasonOfficialsTableColumns.Role, defaultInColumnChooser: true },
      { key: SeasonOfficialsTableColumns.Gender },
      { key: SeasonOfficialsTableColumns.Nationality },
      { key: SeasonOfficialsTableColumns.Status, defaultInColumnChooser: true },
      { key: SeasonOfficialsTableColumns.Bib },
      { key: SeasonOfficialsTableColumns.DateFrom },
      { key: SeasonOfficialsTableColumns.DateTo },
      { key: SeasonOfficialsTableColumns.EntityGroupNameFullLocal },
      { key: SeasonOfficialsTableColumns.EntityNameFullLocal },
      { key: SeasonOfficialsTableColumns.RoleNumber },
      { key: SeasonOfficialsTableColumns.RoleSubType },
      { key: SeasonOfficialsTableColumns.RoleId },
    ],
  },
];

const seasonTableColumnsArray: Array<DataTableColumnGroupDefinition> = [
  {
    columnChooserTitleIntlKey: "seasons.table.column.chooser.title.details",
    columns: [
      { key: SeasonTableColumns.Image },
      { key: SeasonTableColumns.Year, defaultInColumnChooser: true },
      { key: SeasonTableColumns.CompetitionId },
      { key: SeasonTableColumns.CompetitionName, defaultInColumnChooser: true },
      { key: SeasonTableColumns.SeasonId },
      { key: SeasonTableColumns.ExternalId },
      { key: SeasonTableColumns.NameLocal, defaultInColumnChooser: true },
      { key: SeasonTableColumns.Gender, defaultInColumnChooser: true },
      { key: SeasonTableColumns.AgeGroup, defaultInColumnChooser: true },
      { key: SeasonTableColumns.Status, defaultInColumnChooser: true },
      { key: SeasonTableColumns.SeasonType },
      { key: SeasonTableColumns.Representation },
      { key: SeasonTableColumns.Standard },
      { key: SeasonTableColumns.Discipline },
      { key: SeasonTableColumns.StartDate },
      { key: SeasonTableColumns.EndDate },
    ],
  },
];
const seasonEntitiesTableColumnsArray: Array<DataTableColumnGroupDefinition> = [
  {
    columnChooserTitleIntlKey: "seasonEntities.table.column.chooser.title.details",
    columns: [
      { key: SeasonEntityTableColumns.Image },
      { key: SeasonEntityTableColumns.NameFullLocal, defaultInColumnChooser: true },
      { key: SeasonEntityTableColumns.NameFullLatin },
      { key: SeasonEntityTableColumns.CodeLocal },
      { key: SeasonEntityTableColumns.CodeLatin },
      { key: SeasonEntityTableColumns.EntityId },
      { key: SeasonEntityTableColumns.ExternalId },
      { key: SeasonEntityTableColumns.Gender, defaultInColumnChooser: true },
      { key: SeasonEntityTableColumns.AgeGroup, defaultInColumnChooser: true },
      { key: SeasonEntityTableColumns.Standard, defaultInColumnChooser: true },
      { key: SeasonEntityTableColumns.Discipline, defaultInColumnChooser: true },
      { key: SeasonEntityTableColumns.EntityStatus, defaultInColumnChooser: true },
      { key: SeasonEntityTableColumns.Status, defaultInColumnChooser: true },
      { key: SeasonEntityTableColumns.ConferenceName },
      { key: SeasonEntityTableColumns.DivisionName },
      { key: SeasonEntityTableColumns.RosterStatus, defaultInColumnChooser: true },
      { key: SeasonEntityTableColumns.Seed },
    ],
  },
];
const seasonStagesTableColumnsArray: Array<DataTableColumnGroupDefinition> = [
  {
    columnChooserTitleIntlKey: "seasonStages.table.column.chooser.title.details",
    columns: [
      { key: SeasonStageTableColumns.NameLocal, defaultInColumnChooser: true },
      { key: SeasonStageTableColumns.StageCode, defaultInColumnChooser: true },
      { key: SeasonStageTableColumns.StageOrder, defaultInColumnChooser: true },
      { key: SeasonStageTableColumns.NameLatin },
      { key: SeasonStageTableColumns.AbbreviationLocal },
      { key: SeasonStageTableColumns.AbbreviationLatin },
      { key: SeasonStageTableColumns.ExternalId },
    ],
  },
];
const seasonPoolsTableColumnsArray: Array<DataTableColumnGroupDefinition> = [
  {
    columnChooserTitleIntlKey: "seasonPools.table.column.chooser.title.details",
    columns: [
      { key: SeasonPoolTableColumns.NameLocal, defaultInColumnChooser: true },
      { key: SeasonPoolTableColumns.AbbreviationLocal },
      { key: SeasonPoolTableColumns.AbbreviationLatin },
      { key: SeasonPoolTableColumns.PoolCode, defaultInColumnChooser: true },
      { key: SeasonPoolTableColumns.PoolOrder, defaultInColumnChooser: true },
      { key: SeasonPoolTableColumns.StageName, defaultInColumnChooser: true },
      { key: SeasonPoolTableColumns.ExternalId },
      { key: SeasonPoolTableColumns.NameLatin },
    ],
  },
];
const venuesTableColumnsArray: Array<DataTableColumnGroupDefinition> = [
  {
    columnChooserTitleIntlKey: "venues.table.column.chooser.title.details",
    columns: [
      { key: VenueTableColumns.NameLocal, defaultInColumnChooser: true },
      { key: VenueTableColumns.NameLatin },
      { key: VenueTableColumns.AbbreviationLocal },
      { key: VenueTableColumns.AbbreviationLatin },
      { key: VenueTableColumns.VenueId, defaultInColumnChooser: true },
      { key: VenueTableColumns.ExternalId, defaultInColumnChooser: true },
      { key: VenueTableColumns.CountryCode, defaultInColumnChooser: true },
      { key: VenueTableColumns.TimeZone, defaultInColumnChooser: true },
      { key: VenueTableColumns.Capacity, defaultInColumnChooser: true },
      { key: VenueTableColumns.Status, defaultInColumnChooser: true },
    ],
  },
];
const leaguesTableColumnsArray: Array<DataTableColumnGroupDefinition> = [
  {
    columnChooserTitleIntlKey: "leagues.table.column.chooser.title.details",
    columns: [
      { key: LeagueTableColumns.Logo },
      { key: LeagueTableColumns.NameLocal, defaultInColumnChooser: true },
      { key: LeagueTableColumns.NameLatin },
      { key: LeagueTableColumns.AbbreviationLocal },
      { key: LeagueTableColumns.AbbreviationLatin },
      { key: LeagueTableColumns.RegionType, defaultInColumnChooser: true },
      { key: LeagueTableColumns.CountryCode, defaultInColumnChooser: true },
      { key: LeagueTableColumns.ExternalId },
      { key: LeagueTableColumns.LeagueId },
    ],
  },
];
const sitesTableColumnsArray: Array<DataTableColumnGroupDefinition> = [
  {
    columnChooserTitleIntlKey: "sites.table.column.chooser.title.details",
    columns: [
      { key: SiteTableColumns.NameLocal, defaultInColumnChooser: true },
      { key: SiteTableColumns.NameLatin },
      { key: SiteTableColumns.AbbreviationLocal },
      { key: SiteTableColumns.AbbreviationLatin },
      { key: SiteTableColumns.SiteId, defaultInColumnChooser: true },
      { key: SiteTableColumns.ExternalId, defaultInColumnChooser: true },
      { key: SiteTableColumns.CountryCode, defaultInColumnChooser: true },
    ],
  },
];
const standingsConfigurationsTableColumnsArray: Array<DataTableColumnGroupDefinition> = [
  {
    columnChooserTitleIntlKey: "standingsConfigurations.table.column.chooser.title.details",
    columns: [
      { key: StandingsConfigurationTableColumns.NameLocal, defaultInColumnChooser: true },
      { key: StandingsConfigurationTableColumns.NameLatin },
      { key: StandingsConfigurationTableColumns.StandingConfigurationId },
      { key: StandingsConfigurationTableColumns.ExternalId },
    ],
  },
];
const leaderCriteriaTableColumnsArray: Array<DataTableColumnGroupDefinition> = [
  {
    columnChooserTitleIntlKey: "leaderCriteria.table.column.chooser.title.details",
    columns: [{ key: LeaderCriteriaTableColumns.Name, defaultInColumnChooser: true }, { key: LeaderCriteriaTableColumns.LeaderCriteriaId }],
  },
];
const leaderCriteriaRuleTableColumnsArray: Array<DataTableColumnGroupDefinition> = [
  {
    columnChooserTitleIntlKey: "leaderCriteriaRules.table.column.chooser.title.details",
    columns: [
      { key: LeaderCriteriaRuleTableColumns.ComparisonField, defaultInColumnChooser: true },
      { key: LeaderCriteriaRuleTableColumns.ComparisonType, defaultInColumnChooser: true },
      { key: LeaderCriteriaRuleTableColumns.ComparisonValue, defaultInColumnChooser: true },
      { key: LeaderCriteriaRuleTableColumns.StatisticField, defaultInColumnChooser: true },
      { key: LeaderCriteriaRuleTableColumns.QualifierId },
    ],
  },
];
const personsTableColumnsArray: Array<DataTableColumnGroupDefinition> = [
  {
    columnChooserTitleIntlKey: "sites.table.column.chooser.title.details",
    columns: [
      { key: PersonTableColumns.Image },
      { key: PersonTableColumns.NameFullLocal, defaultInColumnChooser: true },
      { key: PersonTableColumns.NameFullLatin },
      { key: PersonTableColumns.NameFamilyLocal },
      { key: PersonTableColumns.NameFamilyLatin },
      { key: PersonTableColumns.NameGivenLocal },
      { key: PersonTableColumns.NameGivenLatin },
      { key: PersonTableColumns.NameAbbreviated },
      { key: PersonTableColumns.KnownAs },
      { key: PersonTableColumns.PersonId, defaultInColumnChooser: true },
      { key: PersonTableColumns.ExternalId, defaultInColumnChooser: true },
      { key: PersonTableColumns.Gender, defaultInColumnChooser: true },
      { key: PersonTableColumns.Nationality, defaultInColumnChooser: true },
      { key: PersonTableColumns.LanguageLocal },
      { key: PersonTableColumns.Representation },
      { key: PersonTableColumns.Dob, defaultInColumnChooser: true },
      { key: PersonTableColumns.Status, defaultInColumnChooser: true },
      { key: PersonTableColumns.Deceased },
      { key: PersonTableColumns.Height },
      { key: PersonTableColumns.Weight },
      { key: PersonTableColumns.HeightImperial },
      { key: PersonTableColumns.WeightImperial },
      { key: PersonTableColumns.JuniorAssociationLeague },
      { key: PersonTableColumns.School },
      { key: PersonTableColumns.SchoolClass },
      { key: PersonTableColumns.HomeTown },
      { key: PersonTableColumns.College },
      { key: PersonTableColumns.CollegeClass },
    ],
  },
];
const entitiesTableColumnsArray: Array<DataTableColumnGroupDefinition> = [
  {
    columnChooserTitleIntlKey: "entities.table.column.chooser.title.details",
    columns: [
      { key: EntityTableColumns.Image },
      { key: EntityTableColumns.NameFullLocal, defaultInColumnChooser: true },
      { key: EntityTableColumns.EntityId, defaultInColumnChooser: true },
      { key: EntityTableColumns.ExternalId, defaultInColumnChooser: true },
      { key: EntityTableColumns.NameFullLatin },
      { key: EntityTableColumns.AgeGroup, defaultInColumnChooser: true },
      { key: EntityTableColumns.Gender, defaultInColumnChooser: true },
      { key: EntityTableColumns.Discipline, defaultInColumnChooser: true },
      { key: EntityTableColumns.Standard, defaultInColumnChooser: true },
      { key: EntityTableColumns.Status, defaultInColumnChooser: true },
      { key: EntityTableColumns.EntityGroupId },
      { key: EntityTableColumns.Representing },
      { key: EntityTableColumns.InternationalReference },
      { key: EntityTableColumns.Grade },
      { key: EntityTableColumns.CodeLocal },
      { key: EntityTableColumns.CodeLatin },
    ],
  },
];

const entityGroupsTableColumnsArray: Array<DataTableColumnGroupDefinition> = [
  {
    columnChooserTitleIntlKey: "entityGroups.table.column.chooser.title.details",
    columns: [
      { key: EntityGroupTableColumns.Logo },
      { key: EntityGroupTableColumns.NameFullLocal, defaultInColumnChooser: true },
      { key: EntityGroupTableColumns.EntityGroupId },
      { key: EntityGroupTableColumns.ExternalId },
      { key: EntityGroupTableColumns.NameFullLatin },
      { key: EntityGroupTableColumns.CodeLatin },
      { key: EntityGroupTableColumns.CodeLocal },
      { key: EntityGroupTableColumns.InternationalReference },
      { key: EntityGroupTableColumns.Status, defaultInColumnChooser: true },
    ],
  },
];

const rosterPersonDetailColumns: DataTableColumnGroupDefinition["columns"] = [
  { key: RosterTableColumns.PersonNameFullLatin },
  { key: RosterTableColumns.PersonAdditionalDetailsHeight },
  { key: RosterTableColumns.PersonAdditionalDetailsWeight },
  { key: RosterTableColumns.PersonAdditionalDetailsHeightImperial },
  { key: RosterTableColumns.PersonAdditionalDetailsWeightImperial },
  { key: RosterTableColumns.PersonAdditionalDetailsRepresentation },
  { key: RosterTableColumns.PersonAdditionalDetailsJuniorAssociationLeague },
  { key: RosterTableColumns.PersonAdditionalDetailsHomeTown },
  { key: RosterTableColumns.PersonAdditionalDetailsCollege },
  { key: RosterTableColumns.PersonAdditionalDetailsCollegeClass },
  { key: RosterTableColumns.PersonAdditionalDetailsSchool },
  { key: RosterTableColumns.PersonAdditionalDetailsSchoolClass },
  { key: RosterTableColumns.Dob },
  { key: RosterTableColumns.PersonGender },
  { key: RosterTableColumns.PersonNationality },
  { key: RosterTableColumns.PersonRepresenting },
  { key: RosterTableColumns.PersonStatus },
];

const seasonRosterTableColumnsArray: Array<DataTableColumnGroupDefinition> = [
  {
    columnChooserTitleIntlKey: "roster.table.column.chooser.title.details",
    columns: [
      { key: RosterTableColumns.Bib, defaultInColumnChooser: true },
      { key: RosterTableColumns.NameFullLocal, defaultInColumnChooser: true },
      { key: RosterTableColumns.PersonId, defaultInColumnChooser: true },
      { key: RosterTableColumns.PersonExternalId, defaultInColumnChooser: true },
      { key: RosterTableColumns.ExternalId, defaultInColumnChooser: true },
      { key: RosterTableColumns.Position, defaultInColumnChooser: true },
      { key: RosterTableColumns.IsCaptain, defaultInColumnChooser: true },
      { key: SeasonRosterTableColumns.Status, defaultInColumnChooser: true },
      ...rosterPersonDetailColumns,
    ],
  },
];

const fixtureRosterTableColumnsArray: Array<DataTableColumnGroupDefinition> = [
  {
    columnChooserTitleIntlKey: "roster.table.column.chooser.title.details",
    columns: [
      { key: RosterTableColumns.Bib, defaultInColumnChooser: true },
      { key: RosterTableColumns.NameFullLocal, defaultInColumnChooser: true },
      { key: RosterTableColumns.PersonId, defaultInColumnChooser: true },
      { key: RosterTableColumns.PersonExternalId, defaultInColumnChooser: true },
      { key: RosterTableColumns.ExternalId, defaultInColumnChooser: true },
      { key: RosterTableColumns.Position, defaultInColumnChooser: true },
      { key: FixtureRosterTableColumns.Starter, defaultInColumnChooser: true },
      { key: RosterTableColumns.IsCaptain, defaultInColumnChooser: true },
      ...rosterPersonDetailColumns,
    ],
  },
];
const conductsTableColumnsArray: Array<DataTableColumnGroupDefinition> = [
  {
    columnChooserTitleIntlKey: "conducts.table.column.chooser.title.details",
    columns: [
      { key: ConductTableColumns.ConductId },
      { key: ConductTableColumns.ExternalId },
      { key: ConductTableColumns.Person, defaultInColumnChooser: true },
      { key: ConductTableColumns.Role, defaultInColumnChooser: true },
      { key: ConductTableColumns.Competition, defaultInColumnChooser: true },
      { key: ConductTableColumns.Season, defaultInColumnChooser: true },
      { key: ConductTableColumns.FixtureId, defaultInColumnChooser: true },
      { key: ConductTableColumns.ConductType, defaultInColumnChooser: true },
      { key: ConductTableColumns.Added, defaultInColumnChooser: true },
      { key: ConductTableColumns.Updated, defaultInColumnChooser: true },
      { key: ConductTableColumns.Status, defaultInColumnChooser: true },
      { key: ConductTableColumns.ConductNotes },
      { key: ConductTableColumns.DateOffenceLocal },
      { key: ConductTableColumns.DateSuspendedFrom },
      { key: ConductTableColumns.DateSuspendedTo },
      { key: ConductTableColumns.LifeSentence },
      { key: ConductTableColumns.FineAmount },
      { key: ConductTableColumns.FineCurrency },
      { key: ConductTableColumns.FineStatus },
      { key: ConductTableColumns.DateFinePaidLocal },
    ],
  },
];

const fixturePlayByPlayFlaggedEventTableColumnsArray: Array<DataTableColumnGroupDefinition> = [
  {
    columnChooserTitleIntlKey: "fixturePBPEvent.table.column.chooser.title.details",
    columns: [
      { key: FixturePlayByPlayEventColumns.Fixture, defaultInColumnChooser: true },
      { key: FixturePlayByPlayEventColumns.FixtureStartTime, defaultInColumnChooser: true },
      { key: FixturePlayByPlayEventColumns.FixtureId, defaultInColumnChooser: true },
      { key: FixturePlayByPlayEventColumns.FixtureStatus, defaultInColumnChooser: true },
      { key: FixturePlayByPlayEventColumns.EventId, defaultInColumnChooser: true },
      { key: FixturePlayByPlayEventColumns.PeriodId, defaultInColumnChooser: true },
      { key: FixturePlayByPlayEventColumns.EventTime, defaultInColumnChooser: true },
      { key: FixturePlayByPlayEventColumns.Entity, defaultInColumnChooser: true },
      { key: FixturePlayByPlayEventColumns.EntityId },
      { key: FixturePlayByPlayEventColumns.EventType, defaultInColumnChooser: true },
      { key: FixturePlayByPlayEventColumns.SubType, defaultInColumnChooser: true },
      { key: FixturePlayByPlayEventColumns.Status, defaultInColumnChooser: true },
      { key: FixturePlayByPlayEventColumns.Class },
      { key: FixturePlayByPlayEventColumns.ClientId },
      { key: FixturePlayByPlayEventColumns.Clock },
      { key: FixturePlayByPlayEventColumns.Flagged },
      { key: FixturePlayByPlayEventColumns.Highlight },
      { key: FixturePlayByPlayEventColumns.OfficialId },
      { key: FixturePlayByPlayEventColumns.Person },
      { key: FixturePlayByPlayEventColumns.PersonId },
      { key: FixturePlayByPlayEventColumns.PlayId },
      { key: FixturePlayByPlayEventColumns.Received },
      { key: FixturePlayByPlayEventColumns.Section },
      { key: FixturePlayByPlayEventColumns.Sequence },
      { key: FixturePlayByPlayEventColumns.ShotClock },
      { key: FixturePlayByPlayEventColumns.Success },
      { key: FixturePlayByPlayEventColumns.Date },
      { key: FixturePlayByPlayEventColumns.Topic },
      { key: FixturePlayByPlayEventColumns.Zone },
    ],
  },
];

//return back key config for columns, remove show from filterconfig
export const defaultUiConfig: UiConfiguration = {
  pages: {
    seasonRoster: {
      listPage: {
        columnsConfig: seasonRosterTableColumnsArray,
        importConfig: {
          titleIntlKey: "import.modal.title.seasonRoster",
          descriptionIntlKey: "import.modal.text.seasonRoster",
        },
      },
    },
    fixtureRoster: {
      listPage: {
        columnsConfig: fixtureRosterTableColumnsArray,
        importConfig: {
          titleIntlKey: "import.modal.title.fixtureRoster",
          descriptionIntlKey: "import.modal.text.fixtureRoster",
        },
      },
    },
    seasonEntities: {
      listPage: {
        columnsConfig: seasonEntitiesTableColumnsArray,
        filtersConfig: [
          { filterKey: SeasonEntityTableFilters.Gender },
          { filterKey: SeasonEntityTableFilters.AgeGroup },
          { filterKey: SeasonEntityTableFilters.Standard },
          { filterKey: SeasonEntityTableFilters.Discipline },
          { filterKey: SeasonEntityTableFilters.Status },
        ],
        importConfig: {
          titleIntlKey: "import.modal.title.seasonEntities",
          descriptionIntlKey: "import.modal.text.seasonEntities",
        },
      },
    },
    seasonStages: {
      listPage: {
        columnsConfig: seasonStagesTableColumnsArray,
        importConfig: {
          titleIntlKey: "import.modal.title.seasonStages",
          descriptionIntlKey: "import.modal.text.seasonStages",
        },
      },
    },
    seasonPools: {
      listPage: {
        columnsConfig: seasonPoolsTableColumnsArray,
        importConfig: {
          titleIntlKey: "import.modal.title.seasonPools",
          descriptionIntlKey: "import.modal.text.seasonPools",
        },
      },
    },
    seasonOfficials: {
      listPage: {
        columnsConfig: seasonOfficialsTableColumnsArray,
        filtersConfig: [
          { filterKey: SeasonOfficialsTableFilters.Role },
          { filterKey: SeasonOfficialsTableFilters.Status },
          { filterKey: SeasonOfficialsTableFilters.Gender },
        ],
        importConfig: {
          titleIntlKey: "import.modal.title.seasonOfficials",
          descriptionIntlKey: "import.modal.text.seasonOfficials",
        },
      },
    },
    fixtures: {
      listPage: {
        filtersConfig: [
          { filterKey: FixturesFilterTypes.Stages },
          { filterKey: FixturesFilterTypes.RoundNumber },
          { filterKey: FixturesFilterTypes.Pools },
          { filterKey: FixturesFilterTypes.Competitors },
          { filterKey: FixturesFilterTypes.Venues },
          { filterKey: FixturesFilterTypes.FixtureStatuses },
          { filterKey: FixturesFilterTypes.DateRange },
        ],
        columnsConfig: fixtureTableColumnsArray,
        importConfig: {
          titleIntlKey: "import.modal.title.fixtures",
          descriptionIntlKey: "import.modal.text.fixtures",
        },
      },
    },
    competitions: {
      listPage: {
        columnsConfig: competitionTableColumnsArray,
        filtersConfig: [{ filterKey: CompetitionFilterTypes.AgeGroup }, { filterKey: CompetitionFilterTypes.League }],
        importConfig: {
          titleIntlKey: "import.modal.title.competitions",
          descriptionIntlKey: "import.modal.text.competitions",
        },
      },
    },
    seasons: {
      listPage: {
        columnsConfig: seasonTableColumnsArray,
        filtersConfig: [
          {
            filterKey: SeasonTableFilterTypes.Gender,
          },
          {
            filterKey: SeasonTableFilterTypes.AgeGroup,
          },
          {
            filterKey: SeasonTableFilterTypes.Status,
          },
          {
            filterKey: SeasonTableFilterTypes.Standard,
          },
        ],
        importConfig: {
          titleIntlKey: "import.modal.title.seasons",
          descriptionIntlKey: "import.modal.text.seasons",
        },
      },
    },
    persons: {
      listPage: {
        filtersConfig: [
          {
            filterKey: PersonTableFiltersTypes.Nationality,
          },
          {
            filterKey: PersonTableFiltersTypes.Gender,
          },
          {
            filterKey: PersonTableFiltersTypes.Status,
          },
        ],
        columnsConfig: personsTableColumnsArray,
        importConfig: {
          titleIntlKey: "import.modal.title.persons",
          descriptionIntlKey: "import.modal.text.persons",
        },
      },
    },
    entities: {
      listPage: {
        filtersConfig: [
          {
            filterKey: EntityTableFiltersTypes.Gender,
          },
          {
            filterKey: EntityTableFiltersTypes.AgeGroup,
          },
          {
            filterKey: EntityTableFiltersTypes.Discipline,
          },
          {
            filterKey: EntityTableFiltersTypes.Standard,
          },
          {
            filterKey: EntityTableFiltersTypes.Status,
          },
        ],
        columnsConfig: entitiesTableColumnsArray,
        importConfig: {
          titleIntlKey: "import.modal.title.entities",
          descriptionIntlKey: "import.modal.text.entities",
        },
      },
    },
    entityGroups: {
      listPage: {
        filtersConfig: [
          {
            filterKey: EntityGroupTableFilterTypes.Status,
          },
        ],
        columnsConfig: entityGroupsTableColumnsArray,
        importConfig: {
          titleIntlKey: "import.modal.title.entityGroups",
          descriptionIntlKey: "import.modal.text.entityGroups",
        },
      },
    },
    conducts: {
      listPage: {
        columnsConfig: conductsTableColumnsArray,
        filtersConfig: [
          {
            filterKey: ConductTableFilterTypes.Competition,
          },
          {
            filterKey: ConductTableFilterTypes.Season,
          },
          {
            filterKey: ConductTableFilterTypes.ConductType,
          },
          {
            filterKey: ConductTableFilterTypes.Status,
          },
        ],
      },
    },
    reports: {
      fixturePlayByPlayFlaggedEventList: {
        columnsConfig: fixturePlayByPlayFlaggedEventTableColumnsArray,
        filtersConfig: [
          {
            filterKey: FixturePlayByPlayEventFilters.FixtureStatus,
          },
          {
            filterKey: FixturePlayByPlayEventFilters.DateRange,
          },
        ],
      },
    },
    dashboard: {
      listPage: {
        filtersConfig: [
          {
            filterKey: SeasonTableFilterTypes.CompetitionName,
          },
          {
            filterKey: SeasonTableFilterTypes.Gender,
          },
          {
            filterKey: SeasonTableFilterTypes.AgeGroup,
          },
          {
            filterKey: SeasonTableFilterTypes.Status,
          },
        ],
        columnsConfig: seasonTableColumnsArray,
      },
    },
    seasonUpsert: {
      promotionRelegationRules: {
        enabled: false,
      },
      disciplines: {
        enabled: false,
        isNullable: true,
      },
    },
    entityStatistic: {
      enabled: false,
    },
    leagues: {
      listPage: {
        columnsConfig: leaguesTableColumnsArray,
        filtersConfig: [
          {
            filterKey: LeagueTableFilters.RegionType,
          },
          {
            filterKey: LeagueTableFilters.CountryCode,
          },
        ],
      },
    },
    venues: {
      listPage: {
        columnsConfig: venuesTableColumnsArray,
        importConfig: {
          titleIntlKey: "import.modal.title.venues",
          descriptionIntlKey: "import.modal.text.venues",
        },
        filtersConfig: [
          {
            filterKey: VenueTableFilterTypes.CountryCode,
          },
          {
            filterKey: VenueTableFilterTypes.Status,
          },
        ],
      },
    },
    sites: {
      listPage: {
        columnsConfig: sitesTableColumnsArray,
        importConfig: {
          titleIntlKey: "import.modal.title.sites",
          descriptionIntlKey: "import.modal.text.sites",
        },
        filtersConfig: [
          {
            filterKey: SiteTableFilters.CountryCode,
          },
        ],
      },
    },
    standingsConfigurations: {
      listPage: {
        columnsConfig: standingsConfigurationsTableColumnsArray,
      },
    },
    leaderCriteria: {
      listPage: {
        columnsConfig: leaderCriteriaTableColumnsArray,
      },
      ruleListPage: {
        columnsConfig: leaderCriteriaRuleTableColumnsArray,
      },
    },
  },
};
